import React, { useRef, useState } from "react";

import { Check, ChevronDown, ChevronUp, Eye, Upload } from "lucide-react";

import { stripBundlePrefix } from "@/utils/bundle-name";
import { cn } from "@/utils/ui";

import { DocSorterFileSchema } from "@api/generated/types.gen";

import {
  DeleteFileConfirmation,
  RegenerateFileConfirmation,
} from "@components/FilesListDataTable";
import { Button } from "@components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/ui/tooltip";

import { formatNullableDateToLocal } from "@utils/dateUtils";

import { formatDocumentCategory } from "../utils/classificationMapper";

type DocumentTimelineViewProps = {
  files: DocSorterFileSchema[];
  onViewFile: (file: DocSorterFileSchema | null) => void;
  onViewSummary: (file: DocSorterFileSchema) => void;
  isLoading: boolean;
  selectedDocumentId?: string;
  showSummaryPanel?: boolean;
  caseId?: string;
};

export const DocumentTimelineView = ({
  files,
  onViewFile,
  onViewSummary,
  isLoading,
  selectedDocumentId,
  showSummaryPanel,
}: DocumentTimelineViewProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const toggleExpanded = (fileId: string) => {
    setExpandedItems((prev) =>
      prev.includes(fileId) ? prev.filter((id) => id !== fileId) : [...prev, fileId]
    );
  };

  const sortedFiles = [...files].sort((a, b) => {
    const dateA = a.date ? new Date(a.date).getTime() : 0;
    const dateB = b.date ? new Date(b.date).getTime() : 0;
    return dateB - dateA; // Reverse to get most recent first
  });

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
          <p className="text-sm text-foreground-muted">Loading documents...</p>
        </div>
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="text-center">
          <p className="text-sm text-foreground-muted">No documents found</p>
        </div>
      </div>
    );
  }

  return (
    <section ref={containerRef} className="h-full">
      <header className="p-6 pb-2">
        <h2 className="text-2xl font-bold">Timeline</h2>
      </header>

      <ol className="relative flex flex-col items-center px-6 pb-3">
        {/* Center timeline line */}
        <div
          className="absolute bottom-0 left-1/2 top-0 -ml-[1px] w-[2px] bg-gray-200"
          aria-hidden="true"
        ></div>

        {sortedFiles.map((file, index) => {
          const isSelected = selectedDocumentId === file.id;
          const isExpanded = expandedItems.includes(file.id);
          const isOnLeftSide = index % 2 === 1;
          const hasNoSummary = !file.short_summary;

          return (
            <li
              key={file.id}
              className={cn(
                "relative flex w-full",
                index === 0 ? "mb-6" : "-mt-16",
                hasNoSummary && "-mt-10"
              )}
            >
              {/* Timeline dot */}
              <button
                type="button"
                aria-label={isSelected ? "Deselect document" : "Select document"}
                className={cn(
                  "absolute left-1/2 z-10 -ml-[10px] h-5 w-5 rounded-full",
                  isSelected
                    ? "border-teal-500 bg-teal-500"
                    : "border-2 border-border bg-background-white"
                )}
                onClick={() => onViewFile(isSelected ? null : file)}
              />

              {/* Connecting line from dot to item */}
              {!showSummaryPanel && (
                <div
                  className={cn(
                    "z-5 absolute top-[10px] h-[1px] w-[25px]",
                    isSelected ? "bg-teal-500" : "bg-gray-200",
                    isOnLeftSide ? "left-[calc(50%-25px)]" : "left-1/2"
                  )}
                  aria-hidden="true"
                />
              )}

              <article
                className={cn(
                  "relative z-10 -mt-2 cursor-pointer rounded-[20px] border",
                  isOnLeftSide ? "mr-auto pr-2" : "ml-auto pl-2",
                  "w-[47%]",
                  isSelected ? "border-teal-500 shadow-md" : "border-gray-200"
                )}
                onClick={() => onViewFile(isSelected ? null : file)}
              >
                <div className="p-3">
                  {/* Document header with date and status */}
                  <header className="flex items-center justify-between">
                    <time
                      className="font-medium text-gray-700"
                      dateTime={file.date || ""}
                    >
                      {formatNullableDateToLocal(file.date, "dd/MM/yyyy")}
                    </time>

                    {/* Status icons */}
                    {(file.status === "EXPORTED" || file.status === "EXPORTING") && (
                      <div className="flex items-center">
                        {file.status === "EXPORTED" ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex rounded-full bg-primary-50 p-1">
                                <Check
                                  className="h-3 w-3 text-primary"
                                  aria-hidden="true"
                                />
                                <span className="sr-only">Exported</span>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>This file has been exported to Report Builder.</p>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex rounded-full bg-orange-50 p-1">
                                <Upload
                                  className="h-3 w-3 animate-pulse text-orange-600"
                                  aria-hidden="true"
                                />
                                <span className="sr-only">Exporting</span>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>
                                This file is currently being exported to Edge
                                Compose.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    )}
                  </header>

                  {/* Document body */}
                  <div className="mt-2">
                    {/* Document description */}
                    {file.short_summary ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <p
                            className={cn(
                              "text-sm text-gray-800",
                              !isExpanded && "line-clamp-2 cursor-help"
                            )}
                          >
                            {file.short_summary}
                          </p>
                        </TooltipTrigger>
                        {!isExpanded && (
                          <TooltipContent className="max-w-md">
                            <p className="text-sm">{file.short_summary}</p>
                          </TooltipContent>
                        )}
                      </Tooltip>
                    ) : (
                      <p className="text-sm italic text-gray-800">
                        No description available
                      </p>
                    )}

                    {/* Category */}
                    <dl className="mt-2">
                      <div className="flex items-center text-xs">
                        <dt className="text-gray-500">Category:</dt>
                        <dd className="ml-1 font-medium text-gray-700">
                          {formatDocumentCategory(file.classification)}
                        </dd>
                      </div>
                    </dl>

                    {/* Toggle expand button */}
                    <Button
                      variant="link"
                      size="sm"
                      className="mt-2 h-auto p-0 text-xs font-medium text-primary"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent container click
                        toggleExpanded(file.id);
                      }}
                    >
                      {isExpanded ? (
                        <>
                          See less{" "}
                          <ChevronUp className="ml-1 h-3 w-3" aria-hidden="true" />
                        </>
                      ) : (
                        <>
                          See more{" "}
                          <ChevronDown className="ml-1 h-3 w-3" aria-hidden="true" />
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Expanded content - file details */}
                  {isExpanded && (
                    <div className="mt-2 border-t border-border pt-2">
                      <dl className="space-y-1 text-xs">
                        <div className="flex">
                          <dt className="w-20 text-gray-500">File name:</dt>
                          <dd
                            className="truncate font-medium text-gray-700"
                            title={stripBundlePrefix(file.name)}
                          >
                            {stripBundlePrefix(file.name)}
                          </dd>
                        </div>
                        <div className="flex">
                          <dt className="w-20 text-gray-500">Uploaded date:</dt>
                          <dd className="font-medium text-gray-700">
                            {formatNullableDateToLocal(
                              file.created_at,
                              "dd/MM/yyyy"
                            )}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="mt-3 flex space-x-1 border-t border-border pt-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent container click
                        onViewSummary(file);
                      }}
                      className="h-8 w-8 text-primary"
                      title="View Full Summary"
                    >
                      <Eye className="h-4 w-4" aria-hidden="true" />
                      <span className="sr-only">View Full Summary</span>
                    </Button>

                    <RegenerateFileConfirmation
                      file={{
                        id: file.id,
                        name: file.name,
                        classification: file.classification || "",
                        date: file.date || undefined,
                        case: file.case,
                      }}
                    />

                    <DeleteFileConfirmation
                      file={{
                        id: file.id,
                        name: file.name,
                        classification: file.classification || "",
                        date: file.date || undefined,
                        case: file.case,
                      }}
                    />
                  </div>
                </div>
              </article>
            </li>
          );
        })}
      </ol>
    </section>
  );
};
